import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'caregiver',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const defaultUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
      const API_URL = process.env.REACT_APP_API_URL || defaultUrl;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        toast({
          title: "Message Received! 💙",
          description: "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', role: 'caregiver', message: '' });
      } else {
        setSubmitStatus('error');
        toast({
          title: "Submission failed",
          description: "Please try again later or email us directly.",
          variant: "destructive"
        });
      }
    } catch (err) {
      setSubmitStatus('error');
      toast({
        title: "Connection Error",
        description: "Could not reach the server. Please check your connection.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (submitStatus) setSubmitStatus(null); // Clear status when user types
  };

  return (
    <section
      id="contact"
      className="py-16 relative"
      style={{ background: '#FFFFFF' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ fontFamily: 'Merriweather, serif', color: '#163172' }}
          >
            Start the Conversation
          </h2>
          <p
            className="text-base md:text-lg max-w-2xl mx-auto"
            style={{ color: '#000000' }}
          >
            Whether you're a caregiver, volunteer, or institution, we'd love to hear from you
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Contact form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-bold mb-2" style={{ color: '#163172' }}>
                  Your Name
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Jane Smith"
                  className="rounded-xl py-6 border-[#D6E4F0]"
                  style={{ background: '#F6F6F6' }}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-bold mb-2" style={{ color: '#163172' }}>
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="jane@example.com"
                  className="rounded-xl py-6 border-[#D6E4F0]"
                  style={{ background: '#F6F6F6' }}
                />
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-bold mb-2" style={{ color: '#163172' }}>
                  I am a...
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full rounded-xl py-3 px-4 border border-[#D6E4F0] transition-colors duration-200"
                  style={{ background: '#F6F6F6', color: '#163172' }}
                >
                  <option value="caregiver">Caregiver or Family Member</option>
                  <option value="volunteer">Student or Volunteer</option>
                  {/* <option value="institution">Institution or Care Facility</option> */}
                  <option value="patient">patient</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-bold mb-2" style={{ color: '#163172' }}>
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell us how we can help..."
                  rows={4}
                  className="rounded-xl border-[#D6E4F0]"
                  style={{ background: '#F6F6F6' }}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-xl py-6 text-base font-bold bg-[#163172] text-[#F6F6F6] hover:bg-[#1E56A0] transition-all duration-300"
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </span>
                )}
              </Button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 rounded-xl bg-green-50 text-green-700 border border-green-200 text-center animate-in fade-in slide-in-from-bottom-2">
                  <p className="font-semibold">Message Sent Successfully! 🎉</p>
                  <p className="text-sm">We'll get back to you shortly.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 rounded-xl bg-red-50 text-red-700 border border-red-200 text-center animate-in fade-in slide-in-from-bottom-2">
                  <p className="font-semibold">Failed to send message.</p>
                  <p className="text-sm">Please check your connection and try again.</p>
                </div>
              )}
            </form>
          </div>

          {/* Contact info */}
          <div className="space-y-8">
            <div className="rounded-3xl overflow-hidden shadow-xl border border-[#D6E4F0]">
              <img
                src="https://images.unsplash.com/photo-1762955911431-4c44c7c3f408"
                alt="Caregiver engagement"
                className="w-full h-full object-cover"
                style={{ aspectRatio: '4/3' }}
              />
            </div>

            <div
              className="p-8 rounded-2xl space-y-6 border border-[#D6E4F0]"
              style={{ background: '#F6F6F6' }}
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#D6E4F0' }}>
                  <Mail className="w-6 h-6" style={{ color: '#163172' }} />
                </div>
                <div>
                  <div className="font-bold mb-1" style={{ color: '#163172' }}>Email Us</div>
                  <a href="mailto:rayan.ashish@icloud.com  " className="text-sm hover:underline text-black">
                    rayan.ashish@icloud.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
